/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { useState, useCallback, useMemo, useTransition } from 'react';
import PropTypes from 'prop-types';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import debounce from '@spotahome/marketplace-common/src/utils/debounce';

import useHorizontalDrag from '@spotahome/ui-library/src/hooks/useHorizontalDrag';

import {
  isFilterChecked,
  getEnabledValue,
  getDisabledValue,
  mustShowPrefilters as mustShowPrefiltersUtils
} from './utils/prefilters';

import { PREFILTERS_CONFIG } from './constants';

import IconPreFilterItem from './IconPrefilterItem';

import './PreFilters.scss';

const SCROLL_DEBOUNCE = 500;

const handleMoveStart = () => {
  SoyuzAnalytics.sendGA4Event('prefilter-drag-start', {});
};

const handleScroll = debounce(() => {
  SoyuzAnalytics.sendGA4Event('prefilter-scroll', {});
}, SCROLL_DEBOUNCE);

const PreFilters = ({ filters, onFiltersUpdate }) => {
  const [prefiltersApplied, setPrefiltersApplied] = useState([]);
  const [, startTransition] = useTransition();

  const { containerRef, isDragging, handleMouseDown, handleMouseUp } =
    useHorizontalDrag({
      onMoveStart: handleMoveStart,
      onMove: handleScroll
    });

  const handlePrefilterClick = useCallback(
    filterConfig => () => {
      // Prevent click if user was dragging
      if (isDragging()) {
        return;
      }
      let newFilterValue;
      let filtersSelected = [...prefiltersApplied];

      if (isFilterChecked(filters, filterConfig.filter, filterConfig.value)) {
        SoyuzAnalytics.sendGA4Event('prefilter-remove', {
          method: filterConfig.id
        });

        newFilterValue = getDisabledValue(
          filters,
          filterConfig.filter,
          filterConfig.value
        );
        filtersSelected = filtersSelected.filter(
          filter => filter !== filterConfig.id
        );
      } else {
        SoyuzAnalytics.sendGA4Event('prefilter-select', {
          method: filterConfig.id
        });
        newFilterValue = getEnabledValue(
          filters,
          filterConfig.filter,
          filterConfig.value
        );
        filtersSelected = [...filtersSelected, filterConfig.id];
      }

      setPrefiltersApplied(filtersSelected);
      startTransition(() => {
        onFiltersUpdate({ [filterConfig.filter]: newFilterValue });
      });
    },
    [filters, prefiltersApplied, isDragging, onFiltersUpdate]
  );

  const prefilterItems = useMemo(
    () =>
      PREFILTERS_CONFIG.map(filterConfig => ({
        ...filterConfig,
        checked: isFilterChecked(
          filters,
          filterConfig.filter,
          filterConfig.value
        ),
        onChange: handlePrefilterClick(filterConfig)
      })),
    [filters, handlePrefilterClick]
  );

  return (
    <div className="pre-filters">
      <ul
        className="pre-filters__list"
        onScroll={handleScroll}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        ref={containerRef}
      >
        {prefilterItems.map(({ id, onChange, iconName, checked }) => (
          <li key={id} className="pre-filters__list-item">
            <IconPreFilterItem
              id={id}
              onChange={onChange}
              iconName={iconName}
              checked={checked}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

PreFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  onFiltersUpdate: PropTypes.func.isRequired
};

export const mustShowPrefilters = filters =>
  mustShowPrefiltersUtils(filters, PREFILTERS_CONFIG);

export default PreFilters;
